import {
	FC,
	useState,
	useEffect,
	useCallback,
	useMemo,
	ChangeEvent
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { clx } from 'Utils';
import { JobFilterType, PublishJobFilterItem } from 'Types';
import { FontAwesome, FormInlineCheckbox, SearchFilterGroup } from 'Elements';
import styles from 'partials/feature/search/search-filter-box.module.scss';

interface Props {
	type: JobFilterType;
	list: PublishJobFilterItem[];
	onApplyFilter: (selected: PublishJobFilterItem[]) => void;
	visibleItems?: number;
}

export const JobFilterDynamicData: FC<Props> = ({
	type,
	list,
	onApplyFilter,
	visibleItems
}) => {
	const { t } = useTranslation();
	const location = useLocation();
	const navigate = useNavigate();

	const defaultDisplayCount = visibleItems || 5;
	const [displayCount, setDisplayCount] = useState(defaultDisplayCount);
	const [filteredList, setFilteredList] = useState(list);
	const [selectedFilter, setSelectedFilter] = useState<
		PublishJobFilterItem[]
	>([]);

	// Query manipulation utilities
	const query = new URLSearchParams(location.search);

	const setSearchParams = (params: Record<string, string>) => {
		const updatedQuery = new URLSearchParams(location.search);
		Object.keys(params).forEach((key) => {
			updatedQuery.set(key, params[key]);
		});
		navigate({ search: updatedQuery.toString() }, { replace: true });
	};

	const clearSearchParams = (keys: string[]) => {
		const updatedQuery = new URLSearchParams(location.search);
		keys.forEach((key) => {
			updatedQuery.delete(key);
		});
		navigate({ search: updatedQuery.toString() }, { replace: true });
	};

	const handleMore = () => {
		setDisplayCount(list.length);
	};

	const handleLess = () => {
		setDisplayCount(defaultDisplayCount);
	};

	const getActiveFilters = useMemo(() => {
		return list?.filter((filter) => filter.isChecked) ?? [];
	}, [list]);

	const handleCheckboxChange = (
		item: PublishJobFilterItem,
		isChecked: boolean
	) => {
		const updateSelectedFilter = (
			updatedFilter: PublishJobFilterItem[]
		) => {
			setSelectedFilter(updatedFilter);
			onApplyFilter(updatedFilter);
		};

		const selectedFilterCopy = [...selectedFilter];
		const index = selectedFilterCopy.findIndex(
			(selectedItem) => selectedItem.value === item.value
		);

		if (isChecked) {
			if (index === -1) {
				selectedFilterCopy.push(item);
				updateSelectedFilter(selectedFilterCopy);
			}
		} else {
			if (index !== -1) {
				const tagQuery = (
					typeof query.get('JobTags') === 'string'
						? query.get('JobTags') || ''
						: ''
				)
					.split(',')
					.filter((tag: string) => tag !== item.value.split('|')[0])
					.join(',');

				if (
					typeof query.get('JobTags') === 'string' &&
					query.get('JobTags')?.split(',').length === 1
				) {
					clearSearchParams(['JobTags']);
				} else {
					setSearchParams({
						...Object.fromEntries(query),
						JobTags: tagQuery
					});
				}
				return;
			}
		}
		updateSelectedFilter(selectedFilterCopy);
	};

	const handleFilterMetadata = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const { value } = e.target;
			const newList = value
				? list.filter((item) =>
						item.value.toLowerCase().includes(value.toLowerCase())
				  )
				: list;

			setFilteredList(newList);
		},
		[list]
	);

	useEffect(() => {
		if (list) {
			setSelectedFilter(getActiveFilters);
		}
	}, [list, getActiveFilters]);

	useEffect(() => {
		setFilteredList(list);
	}, [list]);

	return (
		<SearchFilterGroup title={type}>
			<div className={clx(styles.search, 'mb-3')}>
				<FontAwesome
					icon="magnifying-glass"
					size="lg"
					className={clx(styles.search_icon, 'text-primary-light')}
				/>
				<input
					type="search"
					placeholder={t(`placeholder.search`) + ' ' + type}
					className={clx(styles.input, 'w-100')}
					onChange={handleFilterMetadata}
				/>
			</div>
			{filteredList?.slice(0, displayCount).map((item) => {
				if (item.value) {
					return (
						<div
							className={clx(
								styles.checkbox,
								item.isChecked && styles.checked
							)}
							key={item.value}>
							<FormInlineCheckbox
								id={item.value}
								name={item.value}
								className="d-flex pb-1 mb-2"
								isChecked={item.isChecked}
								onChange={(isChecked) => {
									handleCheckboxChange(item, isChecked);
								}}>
								<div className="d-flex align-items-center">
									{item.value.split('|').pop()}
								</div>
							</FormInlineCheckbox>
						</div>
					);
				}
			})}
			{filteredList?.length > displayCount && (
				<div
					role="button"
					className={clx(styles.more, 'position-relative my-4')}
					onClick={handleMore}>
					<span
						className={clx(
							styles.more_btn,
							'position-absolute top-50 start-50 translate-middle d-flex align-items-center justify-content-center'
						)}>
						<span>{t('label.more')}</span>
					</span>
				</div>
			)}
			{filteredList?.length == displayCount &&
				filteredList?.length !== defaultDisplayCount && (
					<div
						role="button"
						className={clx(styles.more, 'position-relative my-4')}
						onClick={handleLess}>
						<span
							className={clx(
								styles.more_btn,
								'position-absolute top-50 start-50 translate-middle d-flex align-items-center justify-content-center'
							)}>
							<span>{t('label.less')}</span>
						</span>
					</div>
				)}
			{filteredList?.length === 0 && (
				<div className="text-gray-3 fs-small text-center">{''}</div>
			)}
		</SearchFilterGroup>
	);
};
